import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';
import {
  getShouldDisableEmployeeReferralStates,
  getShouldEnableReferralProgramV2,
  shouldUseDsoStructure,
} from '~/growthbook';
import { useUserSelector } from '~/hooks/useUserSelector';
import { routes } from '~/routes';
import { useIsWorkerClassificationEnabled } from '~/selectors';
import {
  fetchNotificationSettings,
  HIDE_LINK_REFERRAL_ACCOUNT_POPUP,
  LINK_REFERRAL_ACCOUNT,
  linkReferralAccount,
  logOut,
  LOGOUT_ACTION,
} from '../../actions';
import { createLoadingSelector } from '../../api/selectors';
import { Icon, Row , CardSpinner } from '../../commonComponents';
import { useLoading } from '../../hooks';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useTrackEvent } from '../../hooks/useTrackEvent';
import { POSTHOG_EVENTS } from '~/services/posthog';
import { Colors } from '../../themes/colors';
import '../../themes/global.scss';
// Was .link in navigation.scss — inlined here until this component is migrated to DS
const linkStyle = {
  color: '#435963',
  fontFamily: 'Nunito',
  fontSize: 20,
  fontWeight: 700,
  padding: '20px 0',
  textDecoration: 'none',
  display: 'block',
};
import { RemoteConfigUtils } from '../../utils/RemoteConfigUtils';
import { useAccountInformation } from '../AccountInformation/hooks/useAccountInformation/useAccountInformation';
import { usePaymentExpiration } from '../Inbox/Notification/Reminders/hooks/usePaymentExpiration';
import { useNotificationTypeMap } from '../NotificationSettings/hooks/useNotificationSettings';
import { LinkReferralPopup } from '../Referral/components/LinkReferralPopup';
import ConfirmPopup from '../shared/ConfirmPopup';
import { BestInDentalWinner } from './components/BestInDentalWinner';
import ProfileHeaderCard from './ProfileHeaderCard';

export default function Profile() {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const allowedWcStates = RemoteConfigUtils.getAllowedWcStates();
  const loadingSelector = createLoadingSelector([LOGOUT_ACTION]);
  const [showLogoutPopup, setShowLogoutPopup] = useState();
  const { shouldShowWarning } = useNotificationTypeMap();
  const childOffices = useSelector((state) => state.user.childOffices);
  const user = useUserSelector();
  const notificationSettings = useSelector((state) => state.user.notificationSettings);
  const showNotificationWarning = notificationSettings?.some(({ event, ...settings }) =>
    shouldShowWarning(event, settings),
  );
  const { isValid: isAccountInformationValid } = useAccountInformation();
  const { isPaymentExpired, isPaymentExpiring } = usePaymentExpiration();
  const selectedOfficeId = useSelector((state) => state.user.selectedOfficeId);

  const isLoading = useSelector((state) => loadingSelector(state));

  const shouldBeDisplayedForParent = childOffices?.find((office) =>
    allowedWcStates?.includes(office.address?.[0]?.state),
  );

  const isWorkerClassificationEnabled = useIsWorkerClassificationEnabled();

  const { isLoading: isLinkingReferral } = useLoading([LINK_REFERRAL_ACCOUNT]);
  const { showLinkReferralAccountPopup, linkReferralError } = useAppSelector((state) => state.user);
  const referralLinked = useAppSelector(
    (state) => state.user?.user?.referralId || state.user?.selectedChildOffice?.referralId,
  );
  const stateToDisableReferralProgram = getShouldDisableEmployeeReferralStates();
  const referralScreenDisabled = stateToDisableReferralProgram?.includes(user.address?.[0]?.state);
  const userCompletedShifts = user?.completedShiftCount && user?.completedShiftCount > 0;
  const showReferralLinkMenu =
    getShouldEnableReferralProgramV2() &&
    !referralLinked &&
    !referralScreenDisabled &&
    !userCompletedShifts;

  const showOversightMenu = shouldUseDsoStructure() && !selectedOfficeId;
  const showOfficeMenu = shouldUseDsoStructure() && selectedOfficeId;
  const showDefaultMenu =
    !shouldUseDsoStructure() ||
    (shouldUseDsoStructure() && selectedOfficeId && !childOffices?.length);

  const menuVisibility = {
    profileHeaderCard: showOfficeMenu || showDefaultMenu,
    accountInformation: showOfficeMenu || showDefaultMenu,
    paymentInformation: showOfficeMenu || showDefaultMenu,
    paymentHistory: true,
    invoices: user.is_pre_approved,
    notificationSettings: showOversightMenu || showDefaultMenu,
    workerTypeManagement:
      (showOversightMenu || showDefaultMenu) &&
      childOffices.length > 0 &&
      shouldBeDisplayedForParent,
    workerTypeSettings: (showOfficeMenu || showDefaultMenu) && isWorkerClassificationEnabled,
    sendFeedback: showOfficeMenu || showDefaultMenu,
    about: true,
    referralLink: Boolean((showOfficeMenu || showDefaultMenu) && showReferralLinkMenu),
    logOut: true,
  };

  useEffect(() => {
    dispatch(fetchNotificationSettings());
  }, [dispatch]);

  if (isLoading) {
    return <CardSpinner />;
  }

  return (
    <div className="component_container">
      <div
        className="floating_container"
        style={{
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: '40px 80px',
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <h1 className="global_font f-dark" style={{ alignSelf: 'center' }}>
            Account
          </h1>
          <MenuWrapper show={menuVisibility.profileHeaderCard}>
            <Row gap={32} alignItems="center">
              <ProfileHeaderCard />
              <BestInDentalWinner />
            </Row>
          </MenuWrapper>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              marginTop: 20,
              marginLeft: 20,
            }}
          >
            <MenuWrapper show={menuVisibility.accountInformation}>
              <Link
                data-cy="account-information"
                to="/account/information"
                style={{ ...linkStyle, fontSize: 18 }}
              >
                <Row gap={4} alignItems="center">
                  Account Information
                  {!isAccountInformationValid && (
                    <Icon name="warning-solid" color={Colors.warning_500} />
                  )}
                </Row>
              </Link>
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.paymentInformation}>
              <div className="line" style={{ width: '95%', margin: 0 }} />
              <Link
                data-cy="payment-information"
                to="/account/payment"
                style={{ ...linkStyle, fontSize: 18 }}
              >
                <Row gap={4} alignItems="center">
                  Payment Information
                  {(isPaymentExpired || isPaymentExpiring) && (
                    <Icon
                      name="warning-solid"
                      color={
                        !isPaymentExpired && isPaymentExpiring
                          ? Colors.warning_500
                          : Colors.error_500
                      }
                    />
                  )}
                </Row>
              </Link>
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.paymentHistory}>
              <div className="line" style={{ width: '95%', margin: 0 }} />
              <Link
                data-cy="payment-history"
                to="/dashboard/receipts"
                style={{ ...linkStyle, fontSize: 18 }}
              >
                Payment History
              </Link>
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.invoices}>
              <div className="line" style={{ width: '95%', margin: 0 }} />
              <Link
                data-cy="invoices"
                to="/account/invoices"
                style={{ ...linkStyle, fontSize: 18 }}
              >
                Invoices
              </Link>
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.notificationSettings}>
              <div className="line" style={{ width: '95%', margin: 0 }} />
              <Link
                data-cy="notification-settings"
                to="/dashboard/notifications"
                style={{ ...linkStyle, fontSize: 18 }}
              >
                <Row gap={4} alignItems="center">
                  Notification Settings
                  {showNotificationWarning && (
                    <Icon name="warning-solid" color={Colors.error_500} />
                  )}
                </Row>
              </Link>
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.workerTypeSettings}>
              <div className="line" style={{ width: '95%', margin: 0 }} />
              <Link
                data-cy="worker-type"
                to={routes.workerClassificationType}
                style={{ ...linkStyle, fontSize: 18, display: 'flex' }}
              >
                <Row gap={4} alignItems="center">
                  Worker Type
                  {!user?.workerClassification?.classificationType && (
                    <Icon name="warning-solid" color={Colors.error_500} />
                  )}
                </Row>
              </Link>
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.workerTypeManagement}>
              <div className="line" style={{ width: '95%', margin: 0 }} />
              <Link
                data-cy="worker-type-management"
                to="/account/worker-classification-management"
                style={{ ...linkStyle, display: 'flex', fontSize: 18 }}
              >
                Worker Type Management
              </Link>
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.sendFeedback}>
              <div className="line" style={{ width: '95%', margin: 0 }} />
              <Link
                data-cy="send-feedback"
                to="/account/feedback"
                style={{ ...linkStyle, fontSize: 18 }}
              >
                Send Feedback
              </Link>
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.logOut}>
              <div className="line" style={{ width: '95%', margin: 0 }} />
              <p
                data-cy="logout"
                to="/account"
                style={{ ...linkStyle, margin: 0, cursor: 'pointer', fontSize: 18 }}
                onClick={() => {
                  trackEvent(POSTHOG_EVENTS.OFFICE_LOGOUT_CLICKED);
                  setShowLogoutPopup(true);
                }}
              >
                Log Out
              </p>
            </MenuWrapper>
          </div>
        </div>
      </div>

      {showLogoutPopup && (
        <ConfirmPopup
          title="Confirm Log Out"
          description="Are you sure you would like to log out?"
          leftButtonText="Go Back"
          leftButtonAction={() => setShowLogoutPopup(false)}
          rightButtonText="Log Out"
          rightButtonAction={() => {
            trackEvent(POSTHOG_EVENTS.OFFICE_LOGOUT_CONFIRMED);
            sessionStorage.clear();
            dispatch(logOut());
            setShowLogoutPopup(false);
          }}
          dataCy="logout-confirmation-modal"
          rightButtonDataCy="btn-confirm-logout"
          leftButtonDataCy="btn-cancel-logout"
        />
      )}

      {showLinkReferralAccountPopup && (
        <LinkReferralPopup
          onClosePopup={() => {
            dispatch({ type: HIDE_LINK_REFERRAL_ACCOUNT_POPUP });
          }}
          onLinkAccount={(referralLink) => {
            dispatch(linkReferralAccount(referralLink));
          }}
          errorMessage={linkReferralError}
          isLoading={isLinkingReferral}
        />
      )}
    </div>
  );
}

const MenuWrapper = ({ children, show = true }) => {
  if (!show) {
    return null;
  }
  return children;
};
